
import React, { useState, useEffect } from 'react';
import { ref, onValue } from 'firebase/database';
import { db } from '../../firebase';
import { UserProfile, Order, Review } from '../../types';
import { Search, User, Mail, Phone, Calendar, ShoppingBag, Star, X, Eye, ArrowUpRight, Hash, MessageSquare, Package } from 'lucide-react';

const AdminCustomers: React.FC = () => {
  const [customers, setCustomers] = useState<UserProfile[]>([]);
  const [allOrders, setAllOrders] = useState<Order[]>([]);
  const [allReviews, setAllReviews] = useState<Review[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<UserProfile | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Müşterileri çek
    onValue(ref(db, 'users'), (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setCustomers(Object.values(data));
      }
      setLoading(false);
    });

    // Tüm siparişleri çek (filtreleme için)
    onValue(ref(db, 'orders'), (snapshot) => {
      const data = snapshot.val();
      if (data) setAllOrders(Object.values(data));
    });

    // Tüm yorumları çek (filtreleme için)
    onValue(ref(db, 'reviews'), (snapshot) => {
      const data = snapshot.val();
      if (data) setAllReviews(Object.values(data));
    });
  }, []);

  const filteredCustomers = customers.filter(c => 
    c.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.phone?.includes(searchTerm) ||
    c.uid.includes(searchTerm)
  );

  const customerOrders = selectedCustomer ? allOrders.filter(o => o.userId === selectedCustomer.uid).sort((a,b) => b.createdAt - a.createdAt) : [];
  const customerReviews = selectedCustomer ? allReviews.filter(r => r.userId === selectedCustomer.uid).sort((a,b) => b.createdAt - a.createdAt) : [];
  const totalSpent = customerOrders.reduce((acc, o) => acc + (o.status !== 'cancelled' ? o.total : 0), 0);

  return (
    <div className="p-8">
      <div className="flex flex-col md:flex-row items-center justify-between mb-10 gap-6">
        <div>
          <h1 className="text-3xl font-black text-stone-900">Müşteri Portföyü</h1>
          <p className="text-stone-500 font-medium italic mt-1">Kayıtlı tüm müşteriler ve alışveriş alışkanlıkları.</p>
        </div>

        <div className="relative w-full md:w-96">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={20} />
          <input 
            type="text"
            placeholder="İsim, E-posta, Telefon veya UID ile ara..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full bg-white border border-stone-100 rounded-2xl py-4 pl-12 pr-4 focus:ring-2 focus:ring-amber-500 outline-none text-sm font-medium shadow-sm"
          />
        </div>
      </div>

      <div className="bg-white rounded-[3rem] border border-stone-100 shadow-sm overflow-hidden">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-stone-50 border-b border-stone-100 text-[10px] font-black text-stone-400 uppercase tracking-[0.2em]">
              <th className="px-8 py-6">Müşteri</th>
              <th className="px-8 py-6">İletişim</th>
              <th className="px-8 py-6">Kayıt Tarihi</th>
              <th className="px-8 py-6">İşlemler</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-50">
            {filteredCustomers.length > 0 ? filteredCustomers.map((cust) => (
              <tr key={cust.uid} className="hover:bg-stone-50/50 transition-colors group">
                <td className="px-8 py-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-amber-50 rounded-2xl flex items-center justify-center text-amber-800 font-black border border-stone-100">
                      {cust.displayName?.charAt(0) || 'U'}
                    </div>
                    <div>
                      <p className="font-black text-stone-900">{cust.displayName}</p>
                      <p className="text-[10px] text-stone-400 font-mono uppercase">UID: {cust.uid.slice(0, 12)}...</p>
                    </div>
                  </div>
                </td>
                <td className="px-8 py-6">
                  <div className="space-y-1">
                    <div className="flex items-center text-xs text-stone-600 font-medium"><Mail size={14} className="mr-2 text-stone-300" /> {cust.email}</div>
                    <div className="flex items-center text-xs text-amber-800 font-black"><Phone size={14} className="mr-2 text-stone-300" /> {cust.phone || 'Belirtilmemiş'}</div>
                  </div>
                </td>
                <td className="px-8 py-6">
                  <div className="flex items-center text-xs text-stone-400 font-bold"><Calendar size={14} className="mr-2 text-stone-300" /> {cust.createdAt ? new Date(cust.createdAt).toLocaleDateString('tr-TR') : 'Bilinmiyor'}</div>
                </td>
                <td className="px-8 py-6">
                  <button 
                    onClick={() => setSelectedCustomer(cust)}
                    className="flex items-center space-x-2 bg-stone-900 text-white px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-amber-800 transition-all active:scale-95 shadow-lg shadow-stone-900/10"
                  >
                    <Eye size={14} />
                    <span>İncele</span>
                  </button>
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan={4} className="py-20 text-center text-stone-300 italic">Müşteri bulunamadı.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Detay Modalı */}
      {selectedCustomer && (
        <div className="fixed inset-0 z-[200] bg-stone-900/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-[#FFFCF8] w-full max-w-6xl h-[90vh] rounded-[3.5rem] shadow-2xl flex flex-col overflow-hidden animate-in zoom-in duration-300">
            {/* Modal Header */}
            <div className="p-8 border-b border-stone-100 flex items-center justify-between bg-white">
              <div className="flex items-center space-x-6">
                <div className="w-20 h-20 bg-stone-50 rounded-[2rem] flex items-center justify-center text-3xl text-amber-800 font-black border border-stone-100 shadow-inner">
                  {selectedCustomer.displayName?.charAt(0) || 'U'}
                </div>
                <div>
                  <h2 className="text-2xl font-black text-stone-900">{selectedCustomer.displayName}</h2>
                  <div className="flex items-center space-x-4 mt-1">
                    <span className="text-xs text-stone-400 font-bold flex items-center"><Mail size={12} className="mr-1"/> {selectedCustomer.email}</span>
                    <a href={`tel:${selectedCustomer.phone}`} className="text-xs text-amber-800 font-black flex items-center hover:underline"><Phone size={12} className="mr-1"/> {selectedCustomer.phone || 'Telefon Yok'}</a>
                    <span className="text-[10px] font-mono text-stone-300 uppercase">UID: {selectedCustomer.uid}</span>
                  </div>
                </div>
              </div>
              <button onClick={() => setSelectedCustomer(null)} className="w-12 h-12 bg-stone-50 rounded-full flex items-center justify-center text-stone-400 hover:text-stone-900 transition-all shadow-sm">
                <X size={24} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-10 scrollbar-hide">
              <div className="grid lg:grid-cols-3 gap-10">
                {/* Sol: İstatistikler ve Siparişler */}
                <div className="lg:col-span-2 space-y-10">
                   {/* Mini Stats */}
                   <div className="grid grid-cols-3 gap-6">
                      <div className="bg-white p-6 rounded-[2rem] border border-stone-100 shadow-sm">
                         <div className="text-[10px] font-black text-stone-400 uppercase tracking-widest mb-2">Toplam Harcama</div>
                         <div className="text-2xl font-black text-amber-800">₺{totalSpent.toLocaleString()}</div>
                      </div>
                      <div className="bg-white p-6 rounded-[2rem] border border-stone-100 shadow-sm">
                         <div className="text-[10px] font-black text-stone-400 uppercase tracking-widest mb-2">Sipariş Sayısı</div>
                         <div className="text-2xl font-black text-stone-900">{customerOrders.length}</div>
                      </div>
                      <div className="bg-white p-6 rounded-[2rem] border border-stone-100 shadow-sm">
                         <div className="text-[10px] font-black text-stone-400 uppercase tracking-widest mb-2">Yorum Sayısı</div>
                         <div className="text-2xl font-black text-indigo-800">{customerReviews.length}</div>
                      </div>
                   </div>

                   {/* Order History */}
                   <div className="space-y-6">
                      <h3 className="text-xl font-black text-stone-900 flex items-center"><Package size={24} className="mr-2 text-amber-800" /> Sipariş Geçmişi</h3>
                      <div className="space-y-4">
                        {customerOrders.length > 0 ? customerOrders.map(order => (
                          <div key={order.id} className="bg-white p-6 rounded-3xl border border-stone-50 flex items-center justify-between group hover:border-amber-100 transition-all">
                             <div className="flex items-center space-x-4">
                               <div className="w-12 h-12 bg-stone-50 rounded-xl flex items-center justify-center text-stone-400">
                                  <ShoppingBag size={20} />
                               </div>
                               <div>
                                  <p className="font-black text-stone-900 text-sm">#{order.id.slice(-8).toUpperCase()}</p>
                                  <p className="text-[10px] text-stone-400 font-bold uppercase">{new Date(order.createdAt).toLocaleDateString('tr-TR')} • {order.items.length} Ürün</p>
                               </div>
                             </div>
                             <div className="text-right">
                                <p className="font-black text-amber-800">₺{order.total.toLocaleString()}</p>
                                <span className={`text-[8px] font-black px-2 py-0.5 rounded-full uppercase tracking-tighter ${order.status === 'delivered' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                                  {order.status}
                                </span>
                             </div>
                          </div>
                        )) : (
                          <div className="py-10 text-center text-stone-300 italic text-sm">Hiç siparişi bulunmuyor.</div>
                        )}
                      </div>
                   </div>
                </div>

                {/* Sağ: Yorumlar ve Notlar */}
                <div className="space-y-10">
                   <div className="bg-white p-8 rounded-[2.5rem] border border-stone-100 shadow-sm h-full flex flex-col">
                      <h3 className="text-xl font-black text-stone-900 mb-8 flex items-center"><Star size={24} className="mr-2 text-amber-800" /> Tüm Değerlendirmeleri</h3>
                      <div className="space-y-6 flex-1 overflow-y-auto pr-2 scrollbar-hide">
                         {customerReviews.length > 0 ? customerReviews.map(rev => (
                           <div key={rev.id} className="p-5 bg-stone-50/50 rounded-2xl border border-stone-100">
                              <div className="flex items-center justify-between mb-2">
                                 <div className="flex text-amber-500">
                                    {[1,2,3,4,5].map(s => <Star key={s} size={10} fill={s <= rev.rating ? "currentColor" : "none"} />)}
                                 </div>
                                 <span className="text-[9px] text-stone-300 font-bold">{new Date(rev.createdAt).toLocaleDateString('tr-TR')}</span>
                              </div>
                              <p className="text-xs text-stone-600 italic leading-relaxed">"{rev.comment}"</p>
                              <div className="mt-2 text-[9px] text-stone-300 font-black uppercase">Ürün ID: {rev.productId.slice(0, 8)}...</div>
                           </div>
                         )) : (
                          <div className="py-10 text-center text-stone-200 italic text-sm">Henüz bir yorum yapmamış.</div>
                         )}
                      </div>
                   </div>
                </div>
              </div> 
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCustomers;
